import React, { useState } from "react";
import { Handle, Position } from "reactflow";
import { Split } from "lucide-react";

const ConditionalNode = ({ id, data }) => {
  // 1. Add Hover State
  const [isHovered, setIsHovered] = useState(false);

  // Restored full list of states from the image
  const statusStyles = {
    idle: "border-zinc-400",
    pending: "border-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.3)]",
    queued: "border-blue-400 shadow-[0_0_10px_rgba(96,165,250,0.3)]",
    failed: "border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.3)]",
    success: "border-green-500 shadow-[0_0_10px_rgba(34,197,94,0.3)]",
    running: "animate-[pulse_1s_infinite] border-green-500 shadow-[0_0_15px_rgba(34,197,94,0.4)]",
    approvalNeeded: "animate-[glow_1.5s_infinite] border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.4)]",
    diagnosing: "animate-[pulse_1s_infinite] border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.4)]",
  };

  const status = data?.status || "idle";
  const condition = data?.condition || "Condition?";
  // Extract title for the hover tooltip
  const title = data?.title || "Conditional Node"; 

  return (
    // Wrapper: w-48 (192px) x h-48
    // Added onMouseEnter/Leave for hover logic
    <div 
      className="relative flex items-center justify-center w-48 h-48"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      
      {/* --- HOVER TOOLTIP --- 
          Positioned absolutely above the node (-top-8) so it doesn't affect layout 
      */}
      {isHovered && (
        <div className="absolute -top-8 z-50 bg-zinc-900 text-zinc-100 text-[10px] px-3 py-1.5 rounded-md border border-zinc-700 shadow-xl pointer-events-none whitespace-nowrap">
          <div className="font-bold mb-0.5">{title}</div>
          <div className="font-mono text-zinc-500">ID: {id}</div>
        </div>
      )}

      {/* 1. THE DIAMOND: w-32 (128px) */}
      <div
        className={`
          absolute w-32 h-32 
          bg-[#1e1e1e] 
          border-2 
          transform rotate-45 
          transition-all duration-300
          ${statusStyles[status] || statusStyles.idle}
        `}
      />

      {/* 2. ICON (Positioned explicitly in the top triangle) */}
      <div className="absolute z-10 -translate-x-1/2 pointer-events-none top-10 left-1/2 text-zinc-400">
        <Split size={24} /> 
      </div>

      {/* 3. TEXT (Centered in the "fat" part of the diamond) */}
      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
        {/* Added padding-top to ensure it doesn't touch the icon visually */}
        <p className="pt-6 text-xs text-zinc-200 font-medium leading-snug text-center max-w-[140px] line-clamp-3 overflow-hidden break-words px-2">
          {condition}
        </p>
      </div>

      {/* 4. HANDLES */}
      
      {/* INPUT (Left Tip) */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ left: 6 }} 
        className="!w-3 !h-3 !bg-zinc-400 !border-2 !border-[#1e1e1e]" 
      />

      {/* TRUE (Right Tip) - Green */}
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        style={{ right: 6 }}
        className="!w-3 !h-3 !bg-green-500 !border-2 !border-[#1e1e1e]"
      />

      {/* FALSE (Bottom Tip) - Red */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        style={{ bottom: 6 }}
        className="!w-3 !h-3 !bg-red-500 !border-2 !border-[#1e1e1e]"
      />
    </div>
  );
};

export default ConditionalNode;